import { type TFunction } from "i18next"

import { type Event } from "../../types/types.ts"
import { appointmentTypeLabelKey } from "../constants/appointment-types.ts"

type CalendarEventRecord = Pick<
  Event,
  "lead" | "project" | "contact" | "category"
> & {
  // The meeting's own address (arriving with the meeting-location work);
  // until every stored event carries it, the lead's contact address stands in.
  address?: string | null
}

interface CalendarEventCardInput {
  title?: string | null
  record?: CalendarEventRecord
}

// Null, not a placeholder: an event stored before the type picker has no
// type to name.
const calendarEventTypeLabel = (
  { record }: CalendarEventCardInput,
  t: TFunction
): string | null => {
  const category = record?.category
  if (!category) return null

  return t(appointmentTypeLabelKey(category))
}

// The one place both "is this event tied to a customer" and "what do we call
// them" get decided — `calendarEventCardRecordParts` needs the same value, so
// it reads through this rather than re-deriving it from `record.contact`.
const calendarEventCardCustomerName = ({
  record
}: CalendarEventCardInput): string | null => record?.contact?.customer_name?.trim() || null

// A lead/customer meeting is identified by WHO it's with, not by whatever the
// organizer typed as a title — the customer's own escalation (all short
// events "look the same") is that free-typed titles ("Kitchen walkthrough",
// "Meeting") stop reading as distinct once a card is a few pixels tall. So a
// linked customer always wins the headline slot, even over a real typed
// title; the type still vacates its own slot in that case, same rule as
// standing in for a missing title, since it is now baked into the headline.
const calendarEventCardText = (
  input: CalendarEventCardInput,
  t: TFunction
): { title: string; typeLabel: string | null } => {
  const typeLabel = calendarEventTypeLabel(input, t)
  const customerName = calendarEventCardCustomerName(input)

  if (customerName && typeLabel) {
    return { title: `${typeLabel}: ${customerName}`, typeLabel: null }
  }

  const title = input.title?.trim()
  return title ? { title, typeLabel } : { title: typeLabel || "-", typeLabel: null }
}

// The record name and the person's name are two distinct lines on the card
// (spec: TIME / TYPE / LEAD·PROJECT / PERSON), never joined into one string —
// that was the previous design and read as too dense.
const calendarEventCardRecordParts = (
  { record }: CalendarEventCardInput,
  t: TFunction
): { recordName: string | null; personName: string | null } => {
  const source = record?.project || record?.lead
  if (!source) return { recordName: null, personName: null }

  // The record's own name ("Cole Backyard Renovation") is what someone
  // scanning the grid actually recognizes — its uid is an internal
  // identifier with no meaning at a glance, so it never appears here.
  const kind = record?.project ? "project" : "lead"
  const recordName = source.name?.trim() || t(`calendarEvent.record.${kind}`)
  const personName = calendarEventCardCustomerName({ record })
  return { recordName, personName }
}

// The card's second row. A follow-up is a call, not a visit — it stays a
// one-line card with no address by design.
const calendarEventCardAddress = ({
  record
}: CalendarEventCardInput): string | null => {
  if (record?.category === "FOLLOW_UP") return null

  const own = record?.address?.trim()
  if (own) return own

  const contact = record?.contact
  const fallback = [contact?.address?.trim(), contact?.city?.trim()]
    .filter(Boolean)
    .join(", ")
  return fallback || null
}

export {
  calendarEventCardAddress,
  calendarEventCardCustomerName,
  calendarEventCardRecordParts,
  calendarEventCardText
}
